"use client";

import React, { useMemo, useState } from "react";
import PendingSignsAccordion from "./PendingSignsAccordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import Loader from "../../../../components/loader";
import { DECIMAL_PATTERN } from "../../../../utils/pattern";
import { GetTBSDetailsResEntity, TBSOrderSolutionEntity } from "../../../../interface/tbs-tracker/send-tbs";
import { useParams } from "react-router-dom";
import { toastOptObj } from "../../../../utils/constants";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { toast } from "react-toastify";
import { VerificationReqEntity, VerificationReturnedLocationEntity } from "../../../../interface/tbs-tracker/tbs-store";
import { APIForVerifyTbs, APIGenerateTbsNo, APIReturnExtraItems } from "../../../../service/api-service";
import { handleDownloadFilesFromLinks } from "../../../../service/shared.service";
import { InputTextarea } from "primereact/inputtextarea";
import { buildTbsDetailEntries, formatTbsDetailLabel, getReceivedQtyError, getReceiverAnswerRaw, getReceiverValueError, getVerifyAnswerKind, TBS_RECEIVER_TEXT_MAX_LENGTH } from "../../../../utils/tbs-verification";
import ReceiverImagesUpload, { ReceiverImageItem, uploadPendingReceiverImages } from "./receiver-images-upload";

type SlotField = "inside" | "outside" | "handle";
type SlotSelection = Record<SlotField, boolean>;
const SLOT_FIELDS: SlotField[] = ["inside", "outside", "handle"];

const countSlots = (selection: SlotSelection): number => SLOT_FIELDS.filter((field) => selection[field]).length;

interface PreviewProps {
    onBack: () => void;
    previewData: GetTBSDetailsResEntity;
    onSubmit: (tbsNo: string) => void;
    isVerify?: boolean;
    /** the return after receipt: the form is locked except the items to return, the remarks and the photos */
    isReturnExtra?: boolean;
    isView?: boolean;
}
/** What the receiver types: the three measurements and the received quantity, plus the remarks. */
interface VerificationFormEntity {
    thickness: string;
    qty: string;
    width: string;
    height: string;
    offCutRequired: string;
    receiverRemarks: string;
}

const EMPTY_RECEIVER_FORM: VerificationFormEntity = {
    thickness: "",
    qty: "",
    width: "",
    height: "",
    offCutRequired: "",
    receiverRemarks: "",
};

/** The verify form starts empty; the return after receipt shows (locked) what the receiver already gave when verifying. */
const buildInitialReceiverForm = (previewData: GetTBSDetailsResEntity, isReturnExtra: boolean): VerificationFormEntity => {
    const receiver = previewData.receiverDetails;
    if (!isReturnExtra || !receiver) {
        return { ...EMPTY_RECEIVER_FORM };
    }
    return {
        thickness: getReceiverAnswerRaw("thickness", receiver),
        qty: getReceiverAnswerRaw("qty", receiver),
        width: getReceiverAnswerRaw("width", receiver),
        height: getReceiverAnswerRaw("height", receiver),
        offCutRequired: getReceiverAnswerRaw("offCutRequired", receiver),
        receiverRemarks: "",
    };
};

interface FilePreviewProps {
    label: string;
    fileName?: string;
    fileUrl?: string;
}

const FilePreview = ({
    label,
    fileName,
    fileUrl,
}: FilePreviewProps) => {
    return (
        <div className="form-group">
            <label className="d-block mb-2">{label}</label>

            <div className="border rounded bg-light px-3 py-1 d-flex align-items-center justify-content-between">
                <span className="text-truncate small">
                    {fileName || "-"}
                </span>

                <Button
                    type="button"
                    icon="pi pi-download"
                    text
                    rounded
                    severity="secondary"
                    disabled={!fileUrl}
                    onClick={() =>
                        fileUrl &&
                        handleDownloadFilesFromLinks([fileUrl])
                    }
                />
            </div>
        </div>
    );
};

const PreviewStep: React.FC<PreviewProps> = ({
    previewData,
    onBack,
    onSubmit,
    isVerify = false,
    isReturnExtra = false,
    isView = false,
}) => {
    // the verify form and the return after receipt are both the receiver's form
    const isReceiverForm = isVerify || isReturnExtra;
    const { id } = useParams();
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [verifiedByReceiver, setVerifiedByReceiver] = useState<VerificationFormEntity>(() => buildInitialReceiverForm(previewData, isReturnExtra));
    // optional photos of the received / damaged / returned items
    const [imageItems, setImageItems] = useState<ReceiverImageItem[]>([]);

    // What the sender selected, location by location (these are the items the receiver is verifying).
    const sentLocations = useMemo(
        () => previewData.locations.flatMap((order) =>
            order.allSolutionList
                .filter((item: TBSOrderSolutionEntity) => item.inside || item.outside || item.handle)
                .map((item: TBSOrderSolutionEntity) => ({ orderId: order.orderDetails.orderId, item }))
        ),
        [previewData]
    );

    // Verify form only: what the receiver keeps. Every sent item starts ticked; unticking one sends it back.
    const [keptSelection, setKeptSelection] = useState<Record<string, SlotSelection>>(() =>
        Object.fromEntries(
            sentLocations.map(({ item }) => [
                item.solutionId,
                { inside: !!item.inside, outside: !!item.outside, handle: !!item.handle },
            ])
        )
    );

    const sentCount = sentLocations.reduce(
        (sum, { item }) => sum + countSlots({ inside: !!item.inside, outside: !!item.outside, handle: !!item.handle }),
        0
    );
    const keptCount = Object.values(keptSelection).reduce((sum, selection) => sum + countSlots(selection), 0);
    const returnedCount = isReceiverForm ? sentCount - keptCount : 0;
    // every item unticked (also the only item of a quantity-1 TBS): the whole TBS goes back and is marked "Returned"
    const isAllReturned = isReceiverForm && sentCount > 0 && keptCount === 0;
    // the verify form then receives nothing: its quantity is 0 and needs no typing
    const isNothingReceived = isVerify && isAllReturned;

    // what the sender sent (the items listed are the fallback when the quantity is missing)
    const senderQty = Number(previewData.model.qty) > 0 ? Number(previewData.model.qty) : sentCount;
    // received quantity vs what is still ticked: a short quantity needs the missing items unticked (sender 7, received 3 -> untick 4)
    const qtyError = isVerify && /^[1-9]\d*$/.test(verifiedByReceiver.qty)
        ? getReceivedQtyError({ receivedQty: Number(verifiedByReceiver.qty), senderQty, sentCount, keptCount })
        : "";

    const handleKeptChange = (solutionId: string, field: SlotField, value: boolean) => {
        setKeptSelection((prev) => ({
            ...prev,
            [solutionId]: { ...prev[solutionId], [field]: value },
        }));
    };

    // the unticked items, as sent to the server
    const getReturnedLocations = (): VerificationReturnedLocationEntity[] =>
        sentLocations
            .map(({ item }) => {
                const kept = keptSelection[item.solutionId];
                return {
                    solutionId: item.solutionId,
                    inside: !!item.inside && !kept?.inside,
                    outside: !!item.outside && !kept?.outside,
                    handle: !!item.handle && !kept?.handle,
                };
            })
            .filter((location) => location.inside || location.outside || location.handle);

    const detailEntries = useMemo(() => buildTbsDetailEntries(previewData), [previewData]);
    const senderValueByField: Record<string, string | number> = Object.fromEntries(detailEntries);

    const showFormValueError = (
        field: keyof VerificationFormEntity
    ) => {
        let message = "";

        // the return after receipt locks the whole form but the items, the remarks and the photos: nothing else to check
        if (isReturnExtra && field !== "receiverRemarks") { return null; }

        switch (field) {
            case "thickness":
            case "width":
            case "height":
                // typed by the receiver: whole numbers in MM
                message = getReceiverValueError(field, getVerifyAnswerKind(field, senderValueByField[field]), verifiedByReceiver[field]);
                break;

            case "qty":
                if (isNothingReceived) {
                    break;
                }
                if (!verifiedByReceiver.qty.trim()) {
                    message = "Please enter quantity!";
                } else if (
                    !DECIMAL_PATTERN.test(verifiedByReceiver.qty)
                ) {
                    message = "Please enter valid quantity!";
                } else if (
                    parseFloat(verifiedByReceiver.qty) < 1 ||
                    parseFloat(verifiedByReceiver.qty) > 100000
                ) {
                    message =
                        "Quantity should be from 1 to 100000";
                } else if (qtyError) {
                    // the received quantity is what the store can issue: it must match what is left ticked
                    message = qtyError;
                }
                break;

            case "offCutRequired":
                if (!verifiedByReceiver.offCutRequired) {
                    message = "Please select OTH with beading!";
                }
                break;

            case "receiverRemarks":
                // optional on the verify form; the return after receipt needs to say why the items went back
                if (isReturnExtra && !verifiedByReceiver.receiverRemarks.trim()) {
                    message = "Please enter return remarks!";
                }
                break;
        }

        if (!isSubmitted || !message) return null;

        return (
            <small
                className="text-danger font-weight-500 p-1"
                data-error="step-error"
                data-field={field}
            >
                {message}
            </small>
        );
    };
    const handleSubmitStep = async () => {
        try {
            setIsLoading(true);

            if (isReceiverForm) {
                const errorElement = document.querySelector(
                    '[data-error="step-error"]'
                );

                if (errorElement) {
                    errorElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    return;
                }
                // the return after receipt is about the unticked items
                if (isReturnExtra && returnedCount === 0) {
                    return;
                }

                // the photos go up first; the form then carries their urls (a retried submit does not upload them again)
                const uploaded = await uploadPendingReceiverImages(id!, imageItems);
                if (!uploaded) return;
                setImageItems(uploaded);
                const imageUrls: string[] = uploaded.map((item) => item.uploadedUrl as string);

                if (isReturnExtra) {
                    const resp = await APIReturnExtraItems({
                        tbsId: id!,
                        notes: verifiedByReceiver.receiverRemarks.trim(),
                        returnedLocations: getReturnedLocations(),
                        images: imageUrls,
                    });
                    if (!resp || !resp.data || resp.status !== 200) return;
                    const apiResp: APIResponseEntity = resp.data;
                    if (apiResp.statusCode == 200 && apiResp.status) {
                        onSubmit(previewData.tbsNumber);
                    } else {
                        toast.error(apiResp.message || apiResp.errorMsg, {
                            ...toastOptObj,
                        });
                    }
                    return;
                }

                // a row that needed no verification (blank / "No" by the sender) has no answer: null
                const numberAnswer = (field: keyof VerificationFormEntity): number | null =>
                    getVerifyAnswerKind(field, senderValueByField[field]) === "number" ? Number(verifiedByReceiver[field]) : null;
                // only thickness, quantity, width and height are asked: the receiver gives no answer for the other rows
                const reqBody: VerificationReqEntity = {
                    tbsId: id!,
                    typeOfTbs: null,
                    category: null,
                    brandName: null,
                    brandCode: null,
                    thickness: numberAnswer("thickness"),
                    qty: isNothingReceived ? "0" : verifiedByReceiver.qty,
                    width: numberAnswer("width"),
                    height: numberAnswer("height"),
                    selectSize: null,
                    anyDrawing: null,
                    offCutRequired: null,
                    receiverRemarks: verifiedByReceiver.receiverRemarks,
                    returnedLocations: getReturnedLocations(),
                    images: imageUrls,
                };
                const resp = await APIForVerifyTbs(reqBody);
                if (!resp || !resp.data || resp.status !== 200) return;
                const apiResp: APIResponseEntity = resp.data;
                if (apiResp.statusCode == 200 && apiResp.data && apiResp.status) {
                    onSubmit(previewData.tbsNumber);
                } else {
                    toast.error(apiResp.message || apiResp.errorMsg, {
                        ...toastOptObj,
                    });
                    return;
                }

            } else {
                const resp = await APIGenerateTbsNo(id!);
                if (!resp || !resp.data || resp.status !== 200) return;
                const apiResp: APIResponseEntity = resp.data;
                if (apiResp.statusCode == 200 && apiResp.data && apiResp.status && apiResp.data.tbsNumber) {
                    onSubmit(apiResp.data.tbsNumber);
                } else {
                    toast.error(apiResp.message || apiResp.errorMsg, {
                        ...toastOptObj,
                    });
                    return;
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Loader isLoading={isLoading} />
            {isReceiverForm && (
                <>
                    <div className="mb-4">
                        <h2 className="mb-0 fw-bold">
                            {isReturnExtra ? "Return Extra Items" : "Verify TBS Items"}
                        </h2>
                    </div>
                    <div className="card mb-4">
                        <div className="card-body py-3">
                            <div className="row">
                                <div className="col-md-3">
                                    <label className="text-muted small">
                                        TBS Number
                                    </label>
                                    <div className="fw-semibold">
                                        {previewData.tbsNumber || "-"}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label className="text-muted small">
                                        Date
                                    </label>
                                    <div className="fw-semibold">
                                        {previewData.tbsDate}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className="card mb-4">
                <div className="card-body p-3">
                    <DataTable
                        value={detailEntries}
                        stripedRows
                        className="p-datatable-sm"
                        tableStyle={{ minWidth: "100%" }}
                    >
                        <Column
                            header="Sr No."
                            body={(_, options) => options.rowIndex + 1}
                            style={{ width: "80px" }}
                        />

                        <Column
                            header="Details"
                            body={(rowData) => formatTbsDetailLabel(rowData[0])}
                        />

                        <Column
                            header="Filled By Sender"
                            body={(rowData) => rowData[1]}
                        />

                        {isReceiverForm && (
                            <Column
                                header="Verified By Receiver"
                                body={(rowData) => {
                                    const field =
                                        rowData[0] as keyof typeof verifiedByReceiver;
                                    // only thickness / width / height (and the quantity below) are typed by the receiver; every other row
                                    // shows "-", and so does a row the sender left blank or answered "No" (nothing to verify)
                                    const answerKind = getVerifyAnswerKind(field, rowData[1]);
                                    // whole numbers in MM, like the quantity
                                    if (answerKind === "number") {
                                        const fieldName = formatTbsDetailLabel(field).toLowerCase();
                                        return (
                                            <>
                                                <InputText
                                                    className="w-100"
                                                    value={verifiedByReceiver[field]}
                                                    disabled={isReturnExtra}
                                                    maxLength={6}
                                                    placeholder={`Enter ${fieldName} in MM`}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (!(value === "" || /^[1-9]\d*$/.test(value))) {
                                                            return;
                                                        }
                                                        setVerifiedByReceiver((prev) => ({
                                                            ...prev,
                                                            [field]: value,
                                                        }));
                                                    }}
                                                />

                                                {showFormValueError(field)}
                                            </>
                                        );
                                    }
                                    if (
                                        ["qty"].includes(field)
                                    ) {
                                        return (
                                            <>
                                                <InputText
                                                    className="w-100"
                                                    value={isNothingReceived ? "0" : verifiedByReceiver[field]}
                                                    disabled={isReturnExtra || isNothingReceived}
                                                    placeholder={`Enter Quantity`}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === "" || /^[1-9]\d*$/.test(value)) {
                                                            setVerifiedByReceiver((prev) => ({
                                                                ...prev,
                                                                [field]: value,
                                                            }));
                                                        }
                                                    }}
                                                />
                                                {returnedCount > 0 && (
                                                    <small className="text-muted d-block mt-1">
                                                        {isNothingReceived
                                                            ? "Every item is returned, so nothing is received."
                                                            : `${keptCount} of ${sentCount} items kept, ${returnedCount} returned`}
                                                    </small>
                                                )}
                                                {/* tells the receiver what to untick while typing; after Submit the same text is the form error below */}
                                                {!isSubmitted && qtyError && (
                                                    <small className="text-danger d-block mt-1">{qtyError}</small>
                                                )}

                                                {showFormValueError(field)}
                                            </>
                                        );
                                    }
                                    return "-";
                                }}
                            />
                        )}
                    </DataTable>
                </div>
            </div>
            {isReceiverForm && (
                <div className="mb-2 small">
                    <span className="text-muted">
                        {isReturnExtra
                            ? "Untick the items you are returning. Unticked items are removed from this TBS and its location, and the sender will see them as returned. Every item can be returned. The rest of this form cannot be changed."
                            : "If you received less than the sender quantity, untick the items that did not arrive. Also untick any item that is broken or not matching. Unticked items are removed from this TBS and its location, and the sender will see them as returned. Untick every item to return the whole TBS."}
                    </span>
                    {returnedCount > 0 && (
                        <strong className="text-danger ms-2">
                            {returnedCount} of {sentCount} {sentCount === 1 ? "item" : "items"} returned
                        </strong>
                    )}
                    {isAllReturned && (
                        <small className="text-danger font-weight-500 d-block mt-1" data-testid="all-returned-note">
                            Every item is returned, so this TBS will be marked as Returned.
                        </small>
                    )}
                    {isReturnExtra && isSubmitted && returnedCount === 0 && (
                        <small
                            className="text-danger font-weight-500 d-block mt-1"
                            data-error="step-error"
                            data-field="returnedItems"
                        >
                            Untick at least one item to return.
                        </small>
                    )}
                </div>
            )}
            {previewData.locations.map((order) => (
                <PendingSignsAccordion
                    key={order.orderDetails.orderId}
                    title={`${order.orderDetails.orderNumber} - ${order.orderDetails.projectName}`}
                    locations={order.allSolutionList.filter(
                        (item) =>
                            item.inside ||
                            item.outside ||
                            item.handle
                    ).map((item) => {
                        const kept = keptSelection[item.solutionId];
                        return {
                            id: item.solutionId,
                            locationNo: item.solutionNo.toString(),
                            category: item.category,
                            system: {
                                wall: item.systemsArr[0],
                                flush: item.systemsArr[1],
                                fireRated: item.systemsArr[2],
                                direction: item.systemsArr[3],
                                code: item.csCode,
                                dropSeal: item.systemsArr[5] ?? "",
                                handle: item.systemsArr[6] ?? "",
                                doorCloser: item.systemsArr[7] ?? "",
                            },
                            specs: {
                                location: item.floor,
                                size: item.specsArr[2],
                                fireRating: item.specsArr[3],
                                frame: item.specsArr[4] ?? "",
                                glassOut: item.specsArr[5] ?? "",
                                glassIn: item.specsArr[6] ?? "",
                            },
                            // verify form: what the receiver still keeps; otherwise what the sender selected
                            inside: isReceiverForm && kept ? kept.inside : item.inside,
                            outside: isReceiverForm && kept ? kept.outside : item.outside,
                            handle: isReceiverForm && kept ? kept.handle : item.handle,
                            showInside: item.TBSglassOneIn,
                            showOutside: item.TBSglassTwoOut,
                            showHandle: item.TBSHandle,
                            sentInside: item.inside,
                            sentOutside: item.outside,
                            sentHandle: item.handle,
                        };
                    })}
                    isReview={!isReceiverForm && isView}
                    isReturnMode={isReceiverForm}
                    onCheckboxChange={(rowId, field, value) => handleKeptChange(rowId, field, value)}
                />
            ))}

            <div className="card my-4">
                <div className="card-body">
                    <h4 className="card-title mb-0">
                        Verify Image
                    </h4>

                    {isReceiverForm && (
                        <div className="row mt-4">
                            {/* <div className="col-md-4">
                                <FilePreview
                                    label="Images"
                                    fileName={previewData.attachments.images.fileName}
                                    fileUrl={previewData.attachments.images.fileUrl}
                                />
                            </div> */}
                            {previewData.dispatch.drawingURL &&
                                <div className="col-md-4">
                                    <FilePreview
                                        label="Drawing"
                                        fileName={previewData.dispatch.drawingURL.split("/").pop()}
                                        fileUrl={previewData.dispatch.drawingURL}
                                    />
                                </div>
                            }
                            <div className="col-md-4">
                                <FilePreview
                                    label="Dispatch Document"
                                    fileName={previewData.dispatch.dispatchDocumentURL.split("/").pop()}
                                    fileUrl={previewData.dispatch.dispatchDocumentURL}
                                />
                            </div>
                        </div>
                    )}

                    <div className="row mt-4">
                        <div className="col-md-2">
                            <div className="form-group">
                                <label className="d-block mb-2">
                                    ZOOM PICTURE
                                </label>

                                <div className="file-card d-flex flex-column align-items-center justify-content-between">
                                    <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                        <Image
                                            src={previewData.dispatch.zoomPicture}
                                            zoomSrc={previewData.dispatch.zoomPicture}
                                            preview
                                            width="130"
                                            height="100"
                                            alt="Zoom Picture"
                                            className="file-card-image"
                                        />
                                    </div>

                                    <div
                                        className="file-card-footer text-center text-truncate w-100"
                                        title={previewData.dispatch.zoomPicture.split("/").pop()}
                                    >
                                        {previewData.dispatch.zoomPicture.split("/").pop()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label className="d-block mb-2">
                                    PACKING PICTURE
                                </label>

                                <div className="file-card d-flex flex-column align-items-center justify-content-between">
                                    <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                        <Image
                                            src={previewData.dispatch.packingPicture}
                                            zoomSrc={previewData.dispatch.packingPicture}
                                            preview
                                            width="130"
                                            height="100"
                                            alt="Packing Picture"
                                            className="file-card-image"
                                        />
                                    </div>

                                    <div
                                        className="file-card-footer text-center text-truncate w-100"
                                        title={previewData.dispatch.packingPicture.split("/").pop()}
                                    >
                                        {previewData.dispatch.packingPicture.split("/").pop()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-8">
                            {isReceiverForm ? (
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="d-block mb-2">
                                                {isReturnExtra ? "RETURN REMARKS" : "REMARKS BY RECEIVER"}
                                                {isReturnExtra && <sup className="mandatory">*</sup>}
                                            </label>

                                            <InputTextarea
                                                className="form-control"
                                                rows={6}
                                                style={{
                                                    minHeight: "130px",
                                                    resize: "none",
                                                }}
                                                maxLength={500}
                                                value={verifiedByReceiver.receiverRemarks}
                                                onChange={(e) =>
                                                    setVerifiedByReceiver((prev) => ({
                                                        ...prev,
                                                        receiverRemarks: e.target.value,
                                                    }))
                                                }
                                            />
                                            {showFormValueError("receiverRemarks")}
                                        </div>
                                    </div>

                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="d-block mb-2">
                                                REMARKS BY SENDER
                                            </label>
                                            <div
                                                className="form-control"
                                                style={{
                                                    minHeight: "130px",
                                                    maxHeight: "200px",
                                                    overflowY: "auto",
                                                    whiteSpace: "pre-wrap",
                                                    backgroundColor: "#e9ecef",
                                                    cursor: "not-allowed",
                                                }}
                                            >
                                                {previewData.dispatch.senderRemarks}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label className="d-block mb-2">
                                        REMARKS BY SENDER
                                    </label>
                                    <div
                                        className="form-control"
                                        style={{
                                            minHeight: "130px",
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            whiteSpace: "pre-wrap",
                                            backgroundColor: "#e9ecef",
                                            cursor: "not-allowed",
                                        }}
                                    >
                                        {previewData.dispatch.senderRemarks}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isReceiverForm && (
                <div className="card my-4">
                    <div className="card-body">
                        <ReceiverImagesUpload
                            title={isReturnExtra ? "IMAGES OF THE RETURNED ITEMS" : "IMAGES OF RECEIVED / DAMAGED ITEMS"}
                            items={imageItems}
                            onChange={setImageItems}
                        />
                    </div>
                </div>
            )}
            <div className="row mt-4">
                <div className="col-md-12 d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium m-0 me-2"
                        onClick={onBack}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="btn btn-success btn-md font-weight-medium m-0"
                        onClick={() => {
                            setIsSubmitted(true);
                            setTimeout(() => {
                                handleSubmitStep();
                            }, 100);
                        }}
                    >
                        {isReturnExtra ? "Return Items" : isVerify ? "Received" : "Submit"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default PreviewStep;